import {
  onSnapshot, query, where, orderBy, limit, getDocs, deleteDoc, runTransaction, writeBatch,
} from 'firebase/firestore';
import { db, collectionRef, paymentDoc } from '../firebase/config';
import { COLLECTIONS, PAYMENT_STATUS, PAYMENT_LIMITS } from '../utils/constants';
import { withRetry } from '../utils/firestoreRetry';
import { FIRESTORE_BATCH_LIMIT } from '../utils/firestoreLimits';
import {
  buildPeriodId, buildPaymentDocId, parsePeriodId, resolveFeeForPeriod,
} from '../utils/paymentPeriods';

/**
 * Aylık ödeme takibi — Firestore erişim katmanı.
 *
 * Veri modeli (kanonik kök: artifacts/{appId}/public/data):
 *   payments/{year}-{month}_{instructorId}_{studentId}
 *
 * Belge kimliği dönem + eğitmen + öğrenciden türetildiği için aynı ay/öğrenci
 * için ikinci kayıt oluşamaz. year / month / period alanları aylık ve yıllık
 * arşiv sorgularını tek indeksle karşılar.
 *
 * Arşiv garantisi: bu modülde otomatik/toplu temizlik yoktur. Silme yalnızca
 * deletePaymentEntry, deletePeriodPayments ve deleteStudentPayments ile,
 * eğitmenin açık isteğiyle olur. Öğrenci silinse / ayrılsa bile kayıtlar
 * sunucuda arşivlenir (archivedAt), silinmez. Her yazma ve silme sunucuda
 * paymentHistory koleksiyonuna eski/yeni hâliyle kaydedilir.
 */

const paymentsCol = () => collectionRef(COLLECTIONS.PAYMENTS);

const mapPaymentDoc = (d) => ({ id: d.id, ...d.data() });

const requireInstructorId = (instructorId) => {
  if (!instructorId) throw new Error('Eğitmen oturumu bulunamadı.');
  return instructorId;
};

const sanitizeAmount = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(Math.round(value * 100) / 100, PAYMENT_LIMITS.MAX_AMOUNT);
};

const sanitizeNote = (note) =>
  typeof note === 'string' ? note.trim().slice(0, PAYMENT_LIMITS.MAX_NOTE_LENGTH) : '';

/** Seçili dönemin canlı dinleyicisi — eğitmenin kendi kayıtları */
export function subscribePeriodPayments({ instructorId, year, month }, onData, onError) {
  const period = buildPeriodId(year, month);
  return onSnapshot(
    query(
      paymentsCol(),
      where('instructorId', '==', requireInstructorId(instructorId)),
      where('period', '==', period)
    ),
    (snapshot) => onData(snapshot.docs.map(mapPaymentDoc)),
    onError
  );
}

/**
 * Anasayfa ve ödeme takibi aynı yıllık sorguyu dinler. Yüzlerce kayıtta her
 * sayfa geçişinde dinleyiciyi baştan kurmak (yerel önbellek taraması + tüm
 * belgelerin yeniden işlenmesi) mobilde saniyeler sürdüğü için tek dinleyici
 * paylaşılır ve son abone ayrıldıktan sonra bir süre açık tutulur.
 */
export const SHARED_YEAR_LISTENER_IDLE_MS = 5 * 60 * 1000;
const sharedYearListeners = new Map();

const disposeSharedYearListener = (key, entry) => {
  clearTimeout(entry.idleTimer);
  entry.idleTimer = null;
  if (sharedYearListeners.get(key) === entry) sharedYearListeners.delete(key);
  entry.unsubscribe?.();
  entry.unsubscribe = null;
};

/** Çıkışta / hesap değişiminde açık bekleyen yıllık dinleyicileri kapatır */
export function releaseSharedPaymentListeners() {
  [...sharedYearListeners.entries()].forEach(([key, entry]) => disposeSharedYearListener(key, entry));
}

/** Yıllık tablonun canlı dinleyicisi — seçili yılın tüm kayıtları */
export function subscribeYearPayments({ instructorId, year }, onData, onError) {
  const id = requireInstructorId(instructorId);
  const numericYear = Number(year);
  const key = `${id}#${numericYear}`;

  let entry = sharedYearListeners.get(key);
  if (!entry) {
    const created = { subscribers: new Set(), entries: null, idleTimer: null, unsubscribe: null };
    sharedYearListeners.set(key, created);
    created.unsubscribe = onSnapshot(
      query(paymentsCol(), where('instructorId', '==', id), where('year', '==', numericYear)),
      (snapshot) => {
        created.entries = snapshot.docs.map(mapPaymentDoc);
        created.subscribers.forEach((s) => s.onData(created.entries));
      },
      (err) => {
        // Hatalı dinleyici Firestore'da kapanır; sonraki abonelik yenisini kurar
        const subscribers = [...created.subscribers];
        disposeSharedYearListener(key, created);
        subscribers.forEach((s) => s.onError?.(err));
      }
    );
    entry = created;
  }

  clearTimeout(entry.idleTimer);
  entry.idleTimer = null;
  const subscriber = { onData, onError };
  entry.subscribers.add(subscriber);
  if (entry.entries) onData(entry.entries);

  const current = entry;
  return () => {
    current.subscribers.delete(subscriber);
    if (current.subscribers.size > 0 || sharedYearListeners.get(key) !== current) return;
    current.idleTimer = setTimeout(
      () => disposeSharedYearListener(key, current),
      SHARED_YEAR_LISTENER_IDLE_MS
    );
  };
}

/** Öğrencinin kendi yıllık ödeme kayıtları (salt okuma) */
export function subscribeStudentYearPayments({ studentId, year }, onData, onError) {
  if (!studentId) throw new Error('Öğrenci oturumu bulunamadı.');
  return onSnapshot(
    query(
      paymentsCol(),
      where('studentId', '==', studentId),
      where('year', '==', Number(year))
    ),
    (snapshot) => onData(snapshot.docs.map(mapPaymentDoc)),
    onError
  );
}

/** Yıllık arşiv — seçili yılın tüm ödeme kayıtları (tek seferlik okuma) */
export async function fetchYearPayments({ instructorId, year }) {
  const snapshot = await getDocs(
    query(
      paymentsCol(),
      where('instructorId', '==', requireInstructorId(instructorId)),
      where('year', '==', Number(year))
    )
  );
  return snapshot.docs.map(mapPaymentDoc);
}

/** Arşivin başladığı yıl — yıl seçicisini doğru aralıkta üretmek için tek okuma */
export async function fetchEarliestPaymentYear(instructorId) {
  const snapshot = await getDocs(
    query(
      paymentsCol(),
      where('instructorId', '==', requireInstructorId(instructorId)),
      orderBy('period', 'asc'),
      limit(1)
    )
  );
  const first = snapshot.docs[0];
  if (!first) return null;
  const data = first.data();
  const parsed = parsePeriodId(data?.period);
  return parsed?.year ?? (Number.isInteger(data?.year) ? data.year : null);
}

export const PAYMENT_CONFLICT = 'PAYMENT_CONFLICT';

const conflictError = (months) => {
  const err = new Error('Bu ay başka bir cihazda değiştirildi. Güncel hâli yüklendi; kontrol edip tekrar kaydedin.');
  err.code = PAYMENT_CONFLICT;
  err.conflictMonths = months;
  return err;
};

const parseChangePeriod = ({ year, month }) => {
  const periodMonth = Number(month);
  const periodYear = Number(year);
  if (!Number.isInteger(periodYear) || !Number.isInteger(periodMonth)
    || periodMonth < 1 || periodMonth > 12) {
    throw new Error('Geçersiz ödeme dönemi.');
  }
  return { periodYear, periodMonth };
};

const assertStatus = (status) => {
  if (status !== PAYMENT_STATUS.PAID && status !== PAYMENT_STATUS.UNPAID) {
    throw new Error('Geçersiz ödeme durumu.');
  }
};

/**
 * Durum tutarlarla çelişemez: beklenen tutar tanımlıysa tamamı tahsil edilen ay "Ödendi",
 * eksik tahsil edilen ay (kısmi ödeme) "Ödemedi" sayılır. Beklenen 0 ise seçilen durum geçerlidir.
 */
export const reconcileStatus = (status, due, paid) => {
  if (due <= 0) return status;
  return paid >= due ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID;
};

/** Transaction içinde okunan önceki kayda göre yazılacak belgeyi üretir */
const buildPaymentPayload = ({
  student, instructor, periodYear, periodMonth, change, previous, now,
}) => {
  const { status, dueAmount, paidAmount, note } = change;
  const paymentId = buildPaymentDocId(periodYear, periodMonth, instructor.id, student.id);
  const resolvedDue = sanitizeAmount(
    dueAmount === undefined
      ? (previous?.dueAmount !== undefined
        ? previous.dueAmount
        : resolveFeeForPeriod(student, periodYear, periodMonth))
      : dueAmount
  );
  const resolvedPaid = sanitizeAmount(
    paidAmount === undefined
      ? (status === PAYMENT_STATUS.PAID ? resolvedDue : 0)
      : paidAmount
  );
  const finalStatus = reconcileStatus(status, resolvedDue, resolvedPaid);
  const isPaid = finalStatus === PAYMENT_STATUS.PAID;
  // Ödendi ↔ Ödemedi gidip gelince öğrenciye ikinci makbuz gitmesin
  const notifyReceipt = isPaid
    && previous?.status !== PAYMENT_STATUS.PAID
    && !previous?.receiptNotifiedAt;
  const receiptNotifiedAt = previous?.receiptNotifiedAt || (isPaid ? now : null);

  return {
    paymentId,
    finalStatus,
    notifyReceipt,
    payload: {
      id: paymentId,
      studentId: student.id,
      studentName: student.name || previous?.studentName || '',
      studentEmail: student.emailOrPhone || previous?.studentEmail || '',
      studentPhone: student.phoneNumber || previous?.studentPhone || '',
      instructorId: instructor.id,
      instructorEmail: instructor.emailOrPhone || '',
      year: periodYear,
      month: periodMonth,
      period: buildPeriodId(periodYear, periodMonth),
      status: finalStatus,
      dueAmount: resolvedDue,
      paidAmount: resolvedPaid,
      note: note === undefined ? sanitizeNote(previous?.note) : sanitizeNote(note),
      createdAt: previous?.createdAt || now,
      updatedAt: now,
      paidAt: isPaid ? (previous?.paidAt || now) : null,
      ...(receiptNotifiedAt ? { receiptNotifiedAt } : {}),
      ...(previous?.archived ? {
        archived: true,
        archivedAt: previous.archivedAt || now,
        archivedReason: previous.archivedReason || '',
      } : {}),
    },
  };
};

/**
 * expectedUpdatedAt: kullanıcının gördüğü kaydın updatedAt değeri (kayıt yoksa null).
 * Verilirse ve sunucudaki kayıt o arada değişmişse yazılmaz; iki cihazdan
 * düzenlemede sonraki kayıt öncekini fark ettirmeden ezmez.
 */
const isStale = (change, previous) => (
  change.expectedUpdatedAt !== undefined
  && (previous?.updatedAt ?? null) !== (change.expectedUpdatedAt ?? null)
);

/**
 * Ödeme durumunu yazar (manuel düzeltme dahil).
 *
 * dueAmount  → o ay ödenmesi gereken tutar; verilmezse o ay geçerli aylık ücret.
 * paidAmount → tahsil edilen tutar; "Ödendi" işaretlenip verilmezse dueAmount
 *              kadar tahsil edildiği varsayılır (tek tıkla tam ödeme).
 *
 * Transaction: createdAt ve ilk ödeme zamanı korunur, önceki ve yazılan durum döner.
 */
export async function setPaymentStatus({
  student, instructor, year, month, status, dueAmount, paidAmount, note, expectedUpdatedAt,
}) {
  if (!student?.id) throw new Error('Öğrenci bulunamadı.');
  requireInstructorId(instructor?.id);
  assertStatus(status);
  const { periodYear, periodMonth } = parseChangePeriod({ year, month });
  const ref = paymentDoc(buildPaymentDocId(periodYear, periodMonth, instructor.id, student.id));
  const change = { status, dueAmount, paidAmount, note, expectedUpdatedAt };

  // Transaction önce belgeyi okur (yoksa da). Bunun için rules'ta
  // resource == null iken eğitmenin okumasına izin verilmelidir.
  return withRetry(() =>
    runTransaction(db, async (t) => {
      const snap = await t.get(ref);
      const previous = snap.exists() ? snap.data() : null;
      if (isStale(change, previous)) throw conflictError([periodMonth]);
      const {
        paymentId, finalStatus, notifyReceipt, payload,
      } = buildPaymentPayload({
        student, instructor, periodYear, periodMonth, change, previous, now: Date.now(),
      });
      t.set(ref, payload);
      return {
        paymentId, previousStatus: previous?.status || null, status: finalStatus, notifyReceipt,
      };
    })
  );
}

/**
 * Bir öğrencinin birden çok ayını tek transaction'da yazar: ya hepsi kaydolur
 * ya hiçbiri. Herhangi bir ay başka cihazda değiştiyse hiçbir şey yazılmaz.
 * changes: [{ year, month, status, dueAmount, paidAmount, note, expectedUpdatedAt }]
 */
export async function setPaymentStatuses({ student, instructor, changes }) {
  if (!student?.id) throw new Error('Öğrenci bulunamadı.');
  requireInstructorId(instructor?.id);
  const list = (changes || []).map((change) => {
    assertStatus(change.status);
    const { periodYear, periodMonth } = parseChangePeriod(change);
    return {
      change,
      periodYear,
      periodMonth,
      ref: paymentDoc(buildPaymentDocId(periodYear, periodMonth, instructor.id, student.id)),
    };
  });
  if (list.length === 0) return [];

  return withRetry(() =>
    runTransaction(db, async (t) => {
      const snaps = await Promise.all(list.map(({ ref }) => t.get(ref)));
      const previous = snaps.map((snap) => (snap.exists() ? snap.data() : null));
      const stale = list
        .filter((item, index) => isStale(item.change, previous[index]))
        .map((item) => item.periodMonth);
      if (stale.length > 0) throw conflictError(stale);

      const now = Date.now();
      return list.map((item, index) => {
        const {
          paymentId, finalStatus, notifyReceipt, payload,
        } = buildPaymentPayload({
          student, instructor, ...item, previous: previous[index], now,
        });
        t.set(item.ref, payload);
        return {
          paymentId,
          notifyReceipt,
          year: item.periodYear,
          month: item.periodMonth,
          status: finalStatus,
          previousStatus: previous[index]?.status || null,
          paidAmount: payload.paidAmount,
          dueAmount: payload.dueAmount,
        };
      });
    })
  );
}

/** Tek kaydı siler — eğitmenin açık talebiyle */
export async function deletePaymentEntry(paymentId) {
  if (!paymentId) throw new Error('Ödeme kaydı bulunamadı.');
  await deleteDoc(paymentDoc(paymentId));
}

/**
 * Bir dönemin tüm kayıtlarını siler — yalnızca arşivdeki "Dönemi Sil"
 * butonundan, onay alındıktan sonra çağrılır.
 */
export async function deletePeriodPayments({ instructorId, year, month }) {
  const period = buildPeriodId(Number(year), Number(month));
  const snapshot = await getDocs(
    query(
      paymentsCol(),
      where('instructorId', '==', requireInstructorId(instructorId)),
      where('period', '==', period)
    )
  );
  if (snapshot.empty) return 0;

  return deleteDocsInBatches(snapshot.docs);
}

async function deleteDocsInBatches(docs) {
  for (let i = 0; i < docs.length; i += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(db);
    docs.slice(i, i + FIRESTORE_BATCH_LIMIT).forEach((d) => batch.delete(d.ref));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
  return docs.length;
}

/**
 * Ayrılan / silinen öğrencilerin tüm yıllardaki kayıtları. Sunucu, öğrenci eğitmenden
 * ayrılınca veya silinince kayıtları silmek yerine archived=true ile işaretler.
 */
export async function fetchArchivedPayments({ instructorId }) {
  const snapshot = await getDocs(
    query(
      paymentsCol(),
      where('instructorId', '==', requireInstructorId(instructorId)),
      where('archived', '==', true)
    )
  );
  return snapshot.docs.map(mapPaymentDoc);
}

/** Arşivdeki "Ayrılan öğrenciler" bölümünden, onay alındıktan sonra tüm yılların kayıtları silinir */
export async function deleteStudentPayments({ instructorId, studentId }) {
  if (!studentId) throw new Error('Öğrenci bulunamadı.');
  const snapshot = await getDocs(
    query(
      paymentsCol(),
      where('instructorId', '==', requireInstructorId(instructorId)),
      where('studentId', '==', studentId)
    )
  );
  if (snapshot.empty) return 0;
  return deleteDocsInBatches(snapshot.docs);
}
